import { useEffect, useState, useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { API } from "../App";

/**
 * BannerDisplay — rotates active approved banners for a given placement.
 * Records impression on first view (intersection observer), click on click.
 *
 * Props:
 *   placement: "hero" | "sidebar" | "footer" | "browse" | "profile"
 *   className: container styles
 *   rotate: ms between banner rotations (default 10_000)
 */
export default function BannerDisplay({ placement = "hero", className = "", rotate = 10000 }) {
  const [ads, setAds] = useState([]);
  const [index, setIndex] = useState(0);
  const containerRef = useRef(null);
  const seen = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/ads`, { params: { placement, limit: 5 } })
      .then((res) => {
        if (!cancelled) setAds(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [placement]);

  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % ads.length);
    }, rotate);
    return () => clearInterval(t);
  }, [ads, rotate]);

  // Impression tracking via intersection observer
  useEffect(() => {
    if (!containerRef.current || ads.length === 0) return;
    const current = ads[index];
    if (!current || seen.current.has(current.ad_id)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && current && !seen.current.has(current.ad_id)) {
            seen.current.add(current.ad_id);
            axios.post(`${API}/ads/${current.ad_id}/impression`).catch(() => {});
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [ads, index]);

  const handleClick = (ad) => {
    axios.post(`${API}/ads/${ad.ad_id}/click`).catch(() => {});
  };

  if (ads.length === 0) return null;
  const ad = ads[index];

  return (
    <div
      ref={containerRef}
      className={`banner-ad-wrapper ${className}`}
      data-testid={`banner-ad-${placement}`}
    >
      <a
        href={ad.link_url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={() => handleClick(ad)}
        className="block relative overflow-hidden border border-zinc-800 bg-zinc-900 group"
        data-testid={`banner-ad-link-${ad.ad_id}`}
      >
        {ad.image_url ? (
          <img
            src={ad.image_url}
            alt={ad.title}
            className="w-full h-auto object-cover transition-transform group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <PartnerBanner ad={ad} />
        )}
        <div className="absolute top-1 right-2 text-[10px] tracking-widest uppercase text-zinc-500 bg-black/60 px-1.5 py-0.5 rounded">
          Ad
        </div>
        {ad.image_url && ad.company_name && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-1.5 text-xs text-zinc-300">
            {ad.company_name}
          </div>
        )}
      </a>
    </div>
  );
}

function PartnerBanner({ ad }) {
  return (
    <div className="flex items-center gap-4 sm:gap-6 px-4 sm:px-6 py-4 sm:py-5 transition-colors group-hover:bg-zinc-800/60">
      {ad.logo_url && (
        <div className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 bg-white flex items-center justify-center p-2">
          <img src={ad.logo_url} alt={`${ad.company_name} logo`} className="max-h-full max-w-full object-contain" loading="lazy" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-heading text-lg sm:text-xl text-white uppercase tracking-wide truncate">{ad.company_name}</p>
        <p className="text-sm text-zinc-400 truncate">{ad.title}</p>
      </div>
      <span className="hidden sm:inline-flex items-center gap-1 text-sm text-orange-400 whitespace-nowrap group-hover:translate-x-1 transition-transform">
        Visit site <ArrowUpRight className="h-4 w-4" />
      </span>
    </div>
  );
}
